import {
  type StreamLainAnimationController,
  streamLainAnimationController,
} from './streamlain-animation-controller';
import { bindStreamLainDisclosureResize } from './streamlain-disclosure-resize';

/** Native details/summary semantics with cancellable close motion in React and Lit. */
export function bindStreamLainDisclosure(
  details: HTMLDetailsElement,
  controller: StreamLainAnimationController = streamLainAnimationController
): () => void {
  const summary = details.querySelector<HTMLElement>(':scope > summary');
  const body = details.querySelector<HTMLElement>(
    ':scope > .streamlain-inline-disclosure-body'
  );
  if (!summary || !body)
    throw new Error('Disclosure requires summary and body');
  let targetOpen = details.open;
  let expectedNativeOpen = details.open;
  let revision = 0;
  // Opening and closing own the height of the body; in between it follows
  // its content.
  let revealing = false;
  const previousExpanded = summary.getAttribute('aria-expanded');
  // The chevron turns along with the body, on the same motion curve.
  const chevron = summary.querySelector<HTMLElement>(
    '.streamlain-inline-disclosure-chevron'
  );
  let chevronAngle = targetOpen ? 90 : 0;
  const turnChevron = (open: boolean, animate: boolean) => {
    if (!chevron) return;
    const from = chevronAngle;
    const to = open ? 90 : 0;
    const set = (angle: number) => {
      chevronAngle = angle;
      chevron.style.transform = `rotate(${angle}deg)`;
    };
    if (!animate || from === to) {
      controller.cancel(chevron);
      set(to);
      return;
    }
    controller.animateProgress(
      chevron,
      progress => set(from + (to - from) * progress),
      'feedback'
    );
  };
  controller.settleRegion(body, targetOpen);
  summary.setAttribute('aria-expanded', String(targetOpen));
  turnChevron(targetOpen, false);

  const nativeToggle = () => {
    if (details.open === expectedNativeOpen) return;
    revision++;
    targetOpen = details.open;
    expectedNativeOpen = details.open;
    controller.settleRegion(body, targetOpen);
    summary.setAttribute('aria-expanded', String(targetOpen));
    turnChevron(targetOpen, false);
  };

  const activate = (event: MouseEvent) => {
    if (event.defaultPrevented || event.button !== 0) return;
    const target = event.target;
    if (
      target instanceof Element &&
      target.closest('a,button,input,select,textarea,[contenteditable="true"]')
    )
      return;
    event.preventDefault();
    targetOpen = !targetOpen;
    const currentRevision = ++revision;
    const activeElement =
      details.getRootNode() instanceof ShadowRoot
        ? (details.getRootNode() as ShadowRoot).activeElement
        : details.ownerDocument.activeElement;
    if (!targetOpen && activeElement && body.contains(activeElement))
      summary.focus();
    summary.setAttribute('aria-expanded', String(targetOpen));
    turnChevron(targetOpen, true);
    // Keep native content visible until close finishes; the body is inert immediately.
    expectedNativeOpen = true;
    details.open = true;
    revealing = true;
    const handle = controller.reveal(body, targetOpen);
    void handle.finished.then(outcome => {
      if (revision === currentRevision) revealing = false;
      if (outcome !== 'finished' || revision !== currentRevision) return;
      expectedNativeOpen = targetOpen;
      details.open = targetOpen;
    });
  };

  const content = body.querySelector<HTMLElement>(
    ':scope > .streamlain-inline-disclosure-body-inner'
  );
  const releaseResize = content
    ? bindStreamLainDisclosureResize(body, {
        controller,
        content,
        canResize: () => targetOpen && !revealing,
      })
    : undefined;

  summary.addEventListener('click', activate);
  details.addEventListener('toggle', nativeToggle);
  return () => {
    revision++;
    releaseResize?.();
    summary.removeEventListener('click', activate);
    details.removeEventListener('toggle', nativeToggle);
    controller.settleRegion(body, targetOpen);
    turnChevron(targetOpen, false);
    details.open = targetOpen;
    if (previousExpanded === null) summary.removeAttribute('aria-expanded');
    else summary.setAttribute('aria-expanded', previousExpanded);
  };
}
